import React, { useState } from 'react';
import { Target, ExternalLink, RefreshCw, Activity, Terminal, Layout } from 'lucide-react';
import { Button } from '@/components/common/ui/Button';
import { ThreatActor } from '@/mock/threatActors';

export interface EnhancedThreatActor extends ThreatActor {
    graphData?: any;
    isLoadingGraph?: boolean;
}

interface VisualizationPanelProps {
    selectedActor: string | null;
    selectedActorData: EnhancedThreatActor | null; 
    fileLoadErrors: Set<string>;
    onRetryLoad: (id: string) => void;
}

const VisualizationPanel: React.FC<VisualizationPanelProps> = ({
    selectedActor,
    selectedActorData,
    fileLoadErrors = new Set<string>(), 
    onRetryLoad,
}) => {
    const [iframeLoading, setIframeLoading] = useState(true);

    // Resolves the file using the exact fileName property and safely encodes spaces
    const getHtmlFilePath = () => {
        if (!selectedActorData) return '';
        const fileName = selectedActorData.fileName || `${selectedActor}-${selectedActorData.name}.html`;
        return encodeURI(`/threat-actors/threat actors - techniques graphs/${fileName}`);
    };

    const handleIframeError = () => {
        if (selectedActor) {
            onRetryLoad(selectedActor);
        }
    };

    // --- Empty State: No Actor Selected ---
    if (!selectedActor || !selectedActorData) {
        return (
            <div className="flex-1 h-full bg-[#050505] flex items-center justify-center font-mono relative overflow-hidden">
                <div className="absolute w-[500px] h-[500px] border border-[#02DFC2]/5 rounded-full animate-ping pointer-events-none"></div>

                <div className="text-center z-10 p-8">
                    <div className="relative inline-block mb-6">
                        <Target className="w-16 h-16 text-slate-800 mx-auto" />
                        <Activity className="w-8 h-8 text-[#02DFC2]/40 absolute -top-2 -right-2 animate-pulse" />
                    </div>
                    <h3 className="text-[14px] font-bold text-white tracking-[0.3em] uppercase mb-3">
                        Select a Threat Actor
                    </h3>
                    <p className="text-[11px] text-slate-500 max-w-sm mx-auto leading-relaxed uppercase tracking-widest">
                        Choose a threat actor from the list to view their interactive MITRE ATT&CK technique mapping.
                    </p>
                </div>
            </div>
        );
    }

    const htmlPath = getHtmlFilePath();

    // --- Active State: Visualization Loaded ---
    return (
        <div className="flex-1 h-full bg-[#050505] flex flex-col font-mono overflow-hidden">
            {/* Header: Viewport Metadata */}
            <div className="p-4 border-b border-[#1A1D24] bg-[#0A0B0E] flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-4">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <Terminal className="w-3.5 h-3.5 text-[#02DFC2]" />
                            <h3 className="text-[13px] font-bold text-white uppercase tracking-tight">
                                {selectedActorData.id} - {selectedActorData.name}
                            </h3>
                        </div>
                        {selectedActorData.aliases && selectedActorData.aliases.length > 0 && (
                            <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5 truncate max-w-md">
                                Also known as: {selectedActorData.aliases.slice(0, 4).join(', ')}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Button
                        variant="secondary"
                        size="sm"
                        className="bg-[#12151C] border-[#1A1D24] text-slate-400 hover:text-[#02DFC2] transition-all"
                        leftIcon={<ExternalLink className="w-3.5 h-3.5" />}
                        onClick={() => window.open(htmlPath, '_blank')}
                    >
                        Open Full View
                    </Button>
                </div>
            </div>

            {/* Content: The Iframe Viewport */}
            <div className="flex-1 p-4 relative group">
                <div className="absolute inset-4 pointer-events-none border border-[#02DFC2]/10 rounded-lg shadow-[inset_0_0_40px_rgba(0,0,0,0.8)] z-10"></div>

                {/* Set iframe container to bg-white so D3/Vis.js graphs render legibly */}
                <div className="w-full h-full bg-white rounded-lg overflow-hidden border border-[#1A1D24] shadow-inner relative">
                    {!fileLoadErrors.has(selectedActor) ? (
                        <>
                            {iframeLoading && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-0">
                                    <RefreshCw className="w-8 h-8 text-[#02DFC2]/40 animate-spin mb-4" />
                                    <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                                        Rendering Graph Nodes...
                                    </span>
                                </div>
                            )}

                            <iframe
                                key={selectedActorData.id}
                                src={htmlPath}
                                className={`w-full h-full relative z-20 transition-opacity duration-500 ${iframeLoading ? 'opacity-0' : 'opacity-100'}`}
                                title={`${selectedActorData.name} Technique Matrix`}
                                frameBorder="0"
                                onLoad={() => setIframeLoading(false)}
                                style={{ filter: 'contrast(1.05)' }}
                                sandbox="allow-scripts allow-same-origin"
                                onError={handleIframeError}
                            />
                        </>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#0A0B0E] p-8 absolute inset-0 z-30">
                            <div className="text-center max-w-md">
                                <div className="w-12 h-12 bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Layout className="w-6 h-6 text-[#FF3333]" />
                                </div>
                                <h4 className="text-[13px] font-bold text-white uppercase tracking-widest mb-2">
                                    Graph_Link_Failure
                                </h4>
                                <p className="text-[11px] text-slate-500 mb-6 uppercase tracking-widest leading-relaxed">
                                    Unable to load {selectedActorData.fileName}. Verify the file exists in public/threat-actors/.
                                </p>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => onRetryLoad(selectedActor)}
                                    leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
                                >
                                    Reinitialize
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Status Bar */}
            <div className="px-4 py-2 border-t border-[#1A1D24] bg-[#050505] flex items-center justify-between">
                <div className="flex items-center gap-4 text-[9px] font-bold text-slate-600 uppercase tracking-[0.2em]">
                    <span>STATUS: {fileLoadErrors.has(selectedActor) ? 'OFFLINE' : 'ONLINE'}</span>
                    <span>TECHNIQUES: {selectedActorData.techniques?.length || 0}</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-1 h-1 rounded-full bg-[#50A970] animate-pulse"></div>
                    <span className="text-[9px] text-[#50A970] font-bold tracking-widest uppercase">Secured</span>
                </div>
            </div>
        </div>
    );
};

export default VisualizationPanel;